import type { PaletteInstance } from './types';

/**
 * Horse coat palettes. Slot order must match HORSE_SLOTS in
 * sprites/horse.ts. Phase 4's coat genetics maps CoatGenes → one of
 * these ramps (plus dilutions), which is why coats are palettes and
 * never baked into the grids.
 */
export const HORSE_SLOTS = [
  'outline',
  'coatDark',
  'coatMid',
  'coatLight',
  'maneDark',
  'maneLight',
  'hoof',
  'sock',
  'eyeDark',
  'muzzle',
  /** Rim light along the top of the silhouette — see lightPass(). */
  'coatHi',
] as const;

/** Pull a colour toward white without washing out its hue. */
function lighten(color: number, amount: number): number {
  const mix = (channel: number): number =>
    Math.round(channel + (255 - channel) * amount);
  return (
    (mix((color >> 16) & 0xff) << 16) | (mix((color >> 8) & 0xff) << 8) | mix(color & 0xff)
  );
}

function coat(
  id: string,
  coatDark: number,
  coatMid: number,
  coatLight: number,
  maneDark: number,
  maneLight: number,
  options: { outline?: number; sock?: number; muzzle?: number } = {},
): PaletteInstance {
  return {
    id,
    colors: [
      options.outline ?? 0x1f1410,
      coatDark,
      coatMid,
      coatLight,
      maneDark,
      maneLight,
      0x2a201a,
      options.sock ?? 0xe8e0d0,
      0x120d0a,
      options.muzzle ?? coatDark,
      lighten(coatLight, 0.28),
    ],
  };
}

export const COAT_BAY = coat('bay', 0x6b4226, 0x8f5b33, 0xa9713f, 0x201713, 0x38281f, {
  muzzle: 0x77522f,
});
export const COAT_GRAY = coat('gray', 0x8a8a94, 0xacacb6, 0xc9c9d1, 0x5c5c66, 0x7a7a84, {
  muzzle: 0x8e8e98,
});
export const COAT_BLACK = coat('black', 0x2b262b, 0x3a343a, 0x4a434c, 0x171317, 0x282228, {
  muzzle: 0x322d32,
});
export const COAT_PALOMINO = coat('palomino', 0xb98e4e, 0xd0a75f, 0xe2bf78, 0xd8cbb0, 0xefe6cf, {
  muzzle: 0x8a6a42,
});
export const COAT_CHESTNUT = coat('chestnut', 0x7e4423, 0x9c5a30, 0xb56f3c, 0x5e3016, 0x7e4a26);
export const COAT_BUCKSKIN = coat('buckskin', 0x94743e, 0xb08e50, 0xc7a763, 0x1d1713, 0x352a20);
export const COAT_CREMELLO = coat('cremello', 0xd8c8a8, 0xe8dcc0, 0xf4ecd8, 0xe8e0cc, 0xf8f4e8, {
  muzzle: 0xc0ad8e,
});

/** Lane 0 (the player) first. Distinct silhouettes-by-color per lane. */
export const LANE_COATS: readonly PaletteInstance[] = [
  COAT_BAY,
  COAT_GRAY,
  COAT_BLACK,
  COAT_PALOMINO,
  COAT_CHESTNUT,
  COAT_BUCKSKIN,
];

/** Phase 4B: coat GENES map to palettes here — foals visibly inherit
 * color with zero extra art. Keyed by phenotype id so the render layer
 * never touches allele logic. */
export const COAT_BY_PHENOTYPE: Record<string, PaletteInstance> = {
  gray: COAT_GRAY,
  bay: COAT_BAY,
  black: COAT_BLACK,
  chestnut: COAT_CHESTNUT,
  palomino: COAT_PALOMINO,
  buckskin: COAT_BUCKSKIN,
  cremello: COAT_CREMELLO,
};

export function coatPaletteFor(phenotype: string): PaletteInstance {
  return COAT_BY_PHENOTYPE[phenotype] ?? COAT_BAY;
}

/** The blown-tank look: the same coat, drained and dusty. */
export function exhaustedVariant(palette: PaletteInstance): PaletteInstance {
  const darken = (color: number): number => {
    const r = Math.round(((color >> 16) & 0xff) * 0.72);
    const g = Math.round(((color >> 8) & 0xff) * 0.68);
    const b = Math.round((color & 0xff) * 0.66);
    return (r << 16) | (g << 8) | b;
  };
  return { id: `${palette.id}-blown`, colors: palette.colors.map(darken) };
}
